import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'framer-motion';
import type { toFrontendProperty } from '../../../services/ragSearchService';
import { getPropertyDisplayTitle } from '../../../utils/arabicPropertyTitle';
import { RESIDENTIAL_TYPES, COMMERCIAL_TYPES } from '../../../config/propertyFormOptions';
import { getPropertyTypeIconId } from '../../../config/propertyTypeCatalog';
import { getVisibleFieldGroups } from '../../../config/propertyTypeConfig';
import DomainIcon from '../../../components/base/icons/domain/DomainIcon';
import ListingBadges from '../../../components/property/ListingBadges';
import SaudiRiyalSymbol from '../../../components/base/icons/SaudiRiyalSymbol';
import { formatAmount } from '../../../utils/numberFormat';
import { isPriceOnRequest, priceOnRequestLabel } from '../../../utils/propertyUtils';

type FrontendProperty = ReturnType<typeof toFrontendProperty>;

const ALL_TYPES = [...RESIDENTIAL_TYPES, ...COMMERCIAL_TYPES];

// Fallback label for a type the i18n catalog does not know (legacy rows).
function typeLabel(raw: string | undefined, isRTL: boolean): string {
  if (!raw) return '';
  const match = ALL_TYPES.find((o) => o.id.toLowerCase() === raw.toLowerCase());
  if (match) return isRTL ? match.labelAr : match.label;
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}

/** Rent periods the listing form stores; anything else gets no suffix rather than a guess. */
const RENT_PERIODS = new Set(['monthly', 'yearly', 'daily']);

// One metric: a 1.125rem glyph beside 0.9375rem text (the proportion rule).
const METRIC = 'flex items-center gap-1.5 tabular-nums';
const METRIC_ICON = 'shrink-0 text-[var(--pv-color-primary-light)]';

// Compact listing card for the home page: For-Sale/For-Rent pill, the price in
// riyals (the SAMA symbol, Western digits), then beds / baths / area / type as
// the listing's TYPE allows. The whole card is a stretched link to the property.
//
// No "verified" badge: the search payload carries no verification field, so a
// badge here would be the same claim on every card, true or not.
export default function PropertyCardCompact({
  property,
  index = 0,
}: {
  property: FrontendProperty;
  index?: number;
}) {
  const { t, i18n } = useTranslation(['home', 'common', 'search']);
  const isRTL = i18n.dir() === 'rtl';
  const reduce = useReducedMotion();
  const [imageFailed, setImageFailed] = useState(false);

  const isRent = (property.listingType || '').toUpperCase() === 'RENT';
  const onRequest = isPriceOnRequest(property.price);
  const title = getPropertyDisplayTitle(
    {
      title: property.title,
      titleAr: property.titleAr,
      propertyType: property.propertyType,
      listingType: property.listingType,
      city: property.city,
      cityAr: property.cityAr,
      bedrooms: property.bedrooms,
    },
    isRTL,
  );
  const city = isRTL ? property.cityAr || property.city : property.city;
  const areaSqm = property.sqft > 0 ? Math.round(property.sqft / 10.7639) : 0;
  const rentPeriod = isRent && property.rentPeriod && RENT_PERIODS.has(property.rentPeriod) ? property.rentPeriod : null;

  // Bedrooms and bathrooms only where the TYPE has them (land never does,
  // offices have bathrooms but no bedrooms), by the same rules as the search
  // filters (config/propertyTypeConfig). A listing with no type at all shows
  // whatever it carries.
  const type = property.propertyType || '';
  const fields = type ? getVisibleFieldGroups(type) : null;
  const beds = property.bedrooms > 0 && (fields ? fields.bedrooms : true) ? property.bedrooms : 0;
  const baths = property.bathrooms > 0 && (fields ? fields.bathrooms : true) ? property.bathrooms : 0;
  const typeIconId = getPropertyTypeIconId(type);
  const typeName = type
    ? t(`search:search.filters.propertyTypeOptions.${type.toUpperCase()}`, { defaultValue: typeLabel(type, isRTL) })
    : '';
  const showImage = Boolean(property.imageUrl) && !imageFailed;

  return (
    <div className="h-full">
      <motion.article
        className="card-lift group relative flex h-full flex-col overflow-hidden rounded-2xl border border-[var(--pv-border-soft)] bg-white shadow-brand-sm"
        initial={reduce ? false : { opacity: 0, y: 20 }}
        whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 0.5, delay: index * 0.08 }}
      >
        {/* The ring is INSET: this link fills a card that clips its overflow,
            so an outer ring would be cut away and keyboard focus invisible. */}
        <Link
          to={`/property/${property.id}`}
          aria-label={title}
          className="absolute inset-0 z-20 rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--pv-color-primary)]"
        />

        {/* Image, or the type's glyph on a quiet plate when there is none (or it fails to load). */}
        <div className="relative h-52 w-full overflow-hidden bg-[var(--pv-color-surface-sunken)]">
          {showImage ? (
            <img
              src={property.imageUrl}
              alt=""
              width={400}
              height={208}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              onError={() => setImageFailed(true)}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-[#7AC3B7]" aria-hidden="true">
              <DomainIcon id={typeIconId} size="3rem" strokeWidth={1.4} />
            </div>
          )}
          {/* Listing-type pill: neutral ink on white with a hairline (THEME.md §2.5),
              then the listing marks, as on a search card. */}
          <div className="absolute start-3 top-3 end-3 flex flex-wrap items-start gap-1.5">
            <span className="rounded-full bg-white px-2.5 py-1 text-sm font-semibold leading-none text-[var(--pv-color-text)] ring-1 ring-[var(--pv-border-soft)]">
              {isRent ? t('home.v2.featured.forRent') : t('home.v2.featured.forSale')}
            </span>
            <ListingBadges listing={property} earnedLimit={1} />
          </div>
        </div>

        {/* Body */}
        <div className="flex flex-1 flex-col p-4 sm:p-5">
          <p className="flex flex-wrap items-baseline gap-x-1.5 font-display text-xl font-bold leading-snug text-[var(--pv-color-primary)] tabular-nums">
            {onRequest ? (
              <span className="text-base font-semibold text-[var(--pv-color-text)]">{priceOnRequestLabel(isRTL)}</span>
            ) : (
              <>
                <SaudiRiyalSymbol className="h-[1em] w-[1em] shrink-0 self-center" title="" aria-hidden="true" />
                <span>{formatAmount(property.price, { maximumFractionDigits: 0 })}</span>
                <span className="sr-only">{t('common:currency.name')}</span>
                {rentPeriod && (
                  <span className="text-sm font-medium text-[var(--pv-color-text-muted)]">
                    /{t(`home.v2.featured.period.${rentPeriod}`)}
                  </span>
                )}
              </>
            )}
          </p>
          <h3 className="mt-1.5 line-clamp-1 font-display text-base font-semibold text-[var(--pv-color-text)]">{title}</h3>
          {city && (
            <p className="mt-1 flex items-center gap-1 text-sm text-[var(--pv-color-text-muted)]">
              <i className="ri-map-pin-2-line shrink-0 text-base leading-none" aria-hidden="true" />
              <span className="line-clamp-1 min-w-0">{city}</span>
            </p>
          )}

          {(beds > 0 || baths > 0 || areaSqm > 0 || typeName) && (
            <ul role="list" className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-[var(--pv-border-soft)] pt-3 text-[0.9375rem] text-[var(--pv-color-text-muted)]">
              {beds > 0 && (
                <li className={METRIC}>
                  <DomainIcon id="bedroom" size="1.125rem" className={METRIC_ICON} />
                  <span aria-hidden="true">{formatAmount(beds)}</span>
                  <span className="sr-only">{t('home.v2.featured.beds', { count: beds, n: formatAmount(beds) })}</span>
                </li>
              )}
              {baths > 0 && (
                <li className={METRIC}>
                  <DomainIcon id="bathroom" size="1.125rem" className={METRIC_ICON} />
                  <span aria-hidden="true">{formatAmount(baths)}</span>
                  <span className="sr-only">{t('home.v2.featured.baths', { count: baths, n: formatAmount(baths) })}</span>
                </li>
              )}
              {areaSqm > 0 && (
                <li className={METRIC}>
                  <DomainIcon id="area" size="1.125rem" className={METRIC_ICON} />
                  <span className="sr-only">{t('home.v2.featured.area')}</span>
                  <span>
                    {formatAmount(areaSqm)} {t('common:units.sqm')}
                  </span>
                </li>
              )}
              {typeName && (
                <li className="flex min-w-0 items-center gap-1.5">
                  <DomainIcon id={typeIconId} size="1.125rem" className={METRIC_ICON} />
                  <span className="truncate">{typeName}</span>
                </li>
              )}
            </ul>
          )}
        </div>
      </motion.article>
    </div>
  );
}
